import React from 'react';
import { Text, View, Page, Document, Image } from '@react-pdf/renderer';
import { StyleSheet } from '@react-pdf/renderer';

// Compact monochromatic styles matching departure.html
const styles = StyleSheet.create({
  page: {
    padding: 9,
    fontSize: 9.5,
    fontFamily: 'Helvetica',
    lineHeight: 1.25,
  },
  
  /* Header Section */
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: '#d1d5db',
    paddingBottom: 9,
  },
  
  logoSection: {
    flex: 1,
  },
  
  companyLogo: {
    maxHeight: 45,
    width: 'auto',
  },
  
  invoiceDetails: {
    textAlign: 'right',
    flex: 1,
  },
  
  invoiceTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 3,
  },
  
  invoiceMeta: {
    fontSize: 8,
    color: '#6b7280',
  },
  
  invoiceMetaRow: {
    marginBottom: 1,
  },
  
  invoiceMetaLabel: {
    fontWeight: 'bold',
    marginRight: 3,
  },
  
  statusBadge: {
    fontSize: 6,
    fontWeight: 'bold',
    marginTop: 2,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    padding: '1px 4px',
  },
  
  /* Account Section */
  accountSection: {
    backgroundColor: '#f9fafb',
    padding: 3,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 11,
    marginBottom: 7,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderTopColor: '#374151',
    borderBottomColor: '#374151',
    color: '#1f2937',
  },
  
  /* Section Headers */
  sectionHeader: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 4,
    paddingBottom: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#d1d5db',
    marginTop: 5,
  },
  
  /* Compact Table */
  dataTable: {
    marginBottom: 9,
    borderWidth: 0.5,
    borderColor: '#d1d5db',
  },
  
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#d1d5db',
  },
  
  tableRowLast: {
    display: 'flex',
    flexDirection: 'row',
  },
  
  tableHeader: {
    backgroundColor: '#f3f4f6',
    padding: 3,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 8,
    color: '#374151',
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: '#d1d5db',
  },
  
  tableHeaderLast: {
    backgroundColor: '#f3f4f6',
    padding: 3,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 8,
    color: '#374151',
    flex: 1,
  },
  
  tableCell: {
    padding: 3,
    textAlign: 'center',
    backgroundColor: 'white',
    color: '#4b5563',
    fontSize: 8,
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: '#e5e7eb',
  },
  
  tableCellLast: {
    padding: 3,
    textAlign: 'center',
    backgroundColor: 'white',
    color: '#4b5563',
    fontSize: 8,
    flex: 1,
  },
  
  /* Remarks Box */
  remarksBox: {
    backgroundColor: '#f9fafb',
    borderWidth: 0.5,
    borderColor: '#d1d5db',
    borderLeftWidth: 1,
    borderLeftColor: '#6b7280',
    padding: 4,
    marginBottom: 7,
    fontSize: 8,
  },
  
  remarksTitle: {
    fontWeight: 'bold',
    marginBottom: 1,
    color: '#1f2937',
  },
  
  remarksContent: {
    color: '#4b5563',
    lineHeight: 1.2,
    fontSize: 6.5,
  },
  
  /* Two Column Layout */
  twoColumn: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    marginBottom: 6,
  },
  
  detailCard: {
    flex: 1,
    backgroundColor: 'white',
    borderWidth: 0.5,
    borderColor: '#d1d5db',
  },
  
  detailCardFull: {
    backgroundColor: 'white',
    borderWidth: 0.5,
    borderColor: '#d1d5db',
    marginBottom: 8,
  },
  
  cardHeader: {
    backgroundColor: '#f3f4f6',
    padding: 3,
    fontWeight: 'bold',
    fontSize: 9,
    borderBottomWidth: 0.5,
    borderBottomColor: '#d1d5db',
    color: '#1f2937',
  },
  
  cardBody: {
    padding: 4,
  },
  
  detailRow: {
    display: 'flex',
    marginBottom: 2,
    fontSize: 9,
    flexDirection: 'row',
  },
  
  detailRowLast: {
    display: 'flex',
    fontSize: 9,
    flexDirection: 'row',
  },
  
  detailRowStrong: {
    color: '#6b7280',
    fontWeight: 'bold',
    width: 60,
  },
  
  detailRowSpan: {
    color: '#374151',
    fontSize: 8,
  },
  
  /* Service Details Section */
  serviceDetailsSection: {
    marginTop: 4,
    marginBottom: 4,
    padding: 3,
    backgroundColor: '#f8f9fa',
    borderLeftWidth: 2,
    borderLeftColor: '#333333',
  },
  
  serviceDetailsTitle: {
    color: '#333333',
    marginBottom: 2,
    fontSize: 8,
    fontWeight: 'bold',
  },
  
  serviceInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  
  serviceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 1,
    fontSize: 7,
  },
  
  serviceLabel: {
    fontWeight: 'bold',
    color: '#495057',
    width: 80,
  },
  
  serviceValue: {
    color: '#212529',
    textAlign: 'right',
    flex: 1,
  },
  
  featuresList: {
    marginLeft: 8,
    color: '#212529',
    fontSize: 6.5,
  },
  
  featuresListItem: {
    marginBottom: 1,
    fontSize: 6.5,
  },
  
  /* Pricing Information Section */
  pricingInfoSection: {
    marginTop: 4,
    marginBottom: 4,
    padding: 3,
    backgroundColor: '#f0f0f0',
    borderLeftWidth: 2,
    borderLeftColor: '#333333',
  },
  
  pricingInfoTitle: {
    color: '#333333',
    marginBottom: 2,
    fontSize: 8,
    fontWeight: 'bold',
  },
  
  pricingInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  
  pricingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 1,
    fontSize: 7,
  },
  
  pricingLabel: {
    fontWeight: 'bold',
    color: '#495057',
    width: 80,
  },
  
  pricingValue: {
    color: '#212529',
    fontWeight: 'bold',
    textAlign: 'right',
    flex: 1,
    fontSize: 6.5,
  },
  
  /* Total Section */
  totalSection: {
    backgroundColor: '#1f2937',
    color: 'white',
    padding: 3,
    marginBottom: 6,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  
  totalLabel: {
    fontWeight: 'bold',
    fontSize: 8,
    width: 80,
    color: 'white',
  },
  
  totalItem: {
    flex: 1,
    textAlign: 'center',
    fontSize: 6.5,
    color: 'white',
  },
  
  /* Footer */
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 2.5,
    borderTopWidth: 0.5,
    borderTopColor: '#d1d5db',
    fontSize: 7,
  },
  
  companyInfo: {
    lineHeight: 1.3,
    color: '#4b5563',
    flex: 1,
  },
  
  companyInfoStrong: {
    fontSize: 8,
    color: '#1f2937',
    fontWeight: 'bold',
  },
  
  vatInfo: {
    textAlign: 'right',
    fontWeight: 'bold',
    color: '#374151',
    fontSize: 7,
  },
});

const DeparturePDF = ({ booking }) => {
  // Calculate pricing - total amount paid is VAT-inclusive
  const totalAmountPaid = parseFloat(booking.amount || booking.totalPrice || 50.00);
  const basePriceInclusive = parseFloat(booking.basePrice || booking.amount || 50.00);
  const additionalFeesInclusive = parseFloat(booking.additionalFees || 0);
  
  // Calculate net amounts (excluding VAT) from VAT-inclusive amounts
  const basePriceNet = basePriceInclusive / 1.2;
  const additionalFeesNet = additionalFeesInclusive / 1.2;
  const subtotalNet = basePriceNet + additionalFeesNet;
  
  // Calculate VAT amounts
  const baseVatAmount = basePriceNet * 0.2;
  const additionalVatAmount = additionalFeesNet * 0.2;
  const totalVatAmount = subtotalNet * 0.2;

  // Generate reference number
  const referenceNumber = booking._id ? `DEP-${booking._id.slice(-6).toUpperCase()}` : 'DEP-TBD';
  
  // Format dates: prefer departureDate; show date only
  const pickDate = (d) => {
    if (!d) return new Date().toLocaleDateString();
    try {
      if (typeof d === 'string' && d.includes('T')) {
        return new Date(d).toISOString().split('T')[0];
      }
      return d;
    } catch (_) {
      return d;
    }
  };
  const serviceDate = pickDate(booking.departureDate || booking.serviceDate);
  
  // Get flight details
  const flightNumber = booking.flightNumber || 'TBD';
  const fromAirport = booking.from || 'TBD';
  const toAirport = booking.to || 'TBD';
  // Time: prefer mapped departureTime; else derive from ISO
  const timeFromISO = (d) => {
    if (!d || typeof d !== 'string' || !d.includes('T')) return 'TBD';
    try {
      const date = new Date(d);
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch (_) {
      return 'TBD';
    }
  };
  const departureTime = (booking.departureTime && booking.departureTime !== 'TBD') ? booking.departureTime : timeFromISO(booking.departureDate);

  const formatCurrency = (value) => `${parseFloat(value).toFixed(2)}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.logoSection}>
            <Image 
              src="/airportpicture.jpg" 
              style={styles.companyLogo}
              alt="Company Logo"
            />
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.invoiceTitle}>TAX INVOICE</Text>
            <View style={styles.invoiceMeta}>
              <View style={styles.invoiceMetaRow}>
                <Text><Text style={styles.invoiceMetaLabel}>Booking Ref:</Text> {referenceNumber}</Text>
              </View>
              <View style={styles.invoiceMetaRow}>
                <Text><Text style={styles.invoiceMetaLabel}>Date & Time:</Text> {serviceDate}</Text>
              </View>
              <View style={styles.invoiceMetaRow}>
                <Text><Text style={styles.invoiceMetaLabel}>Currency:</Text> USD</Text>
              </View>
              <View style={styles.invoiceMetaRow}>
                <Text><Text style={styles.invoiceMetaLabel}>VAT No:</Text> 177013321</Text>
              </View>
              <View style={styles.invoiceMetaRow}>
                <Text><Text style={styles.invoiceMetaLabel}>Web Order:</Text> {booking.webOrder || 'Yes'}</Text>
              </View>
            </View>
            <Text style={styles.statusBadge}>{booking.status || 'CONFIRMED'}</Text>
          </View>
        </View>
        
        {/* Account Section */}
        <View style={styles.accountSection}>
          <Text>ACCOUNT</Text>
        </View>
        
        {/* Flight Details */}
        <Text style={styles.sectionHeader}>Flight Details</Text>
        <View style={styles.dataTable}>
          <View style={styles.tableRow}>
            <View style={styles.tableHeader}><Text>Service Type</Text></View>
            <View style={styles.tableHeader}><Text>Date</Text></View>
            <View style={styles.tableHeader}><Text>Flight</Text></View>
            <View style={styles.tableHeader}><Text>From/To</Text></View>
            <View style={styles.tableHeader}><Text>Time</Text></View>
            <View style={styles.tableHeaderLast}><Text>Reference</Text></View>
          </View>
          <View style={styles.tableRowLast}>
            <View style={styles.tableCell}><Text>Departure Meet & Greet</Text></View>
            <View style={styles.tableCell}><Text>{serviceDate}</Text></View>
            <View style={styles.tableCell}><Text>{flightNumber}</Text></View>
            <View style={styles.tableCell}><Text>{fromAirport} to {toAirport}</Text></View>
            <View style={styles.tableCell}><Text>{departureTime}</Text></View>
            <View style={styles.tableCellLast}><Text>{referenceNumber}</Text></View>
          </View>
        </View>
        
        {/* Customer Details */}
        <Text style={styles.sectionHeader}>Customer Details</Text>
        <View style={styles.dataTable}>
          <View style={styles.tableRow}>
            <View style={styles.tableHeader}><Text>No</Text></View>
            <View style={styles.tableHeader}><Text>Title</Text></View>
            <View style={styles.tableHeader}><Text>Name</Text></View>
            <View style={styles.tableHeader}><Text>Type</Text></View>
            <View style={styles.tableHeader}><Text>Services</Text></View>
            <View style={styles.tableHeader}><Text>Add On</Text></View>
            <View style={styles.tableHeaderLast}><Text>Notes</Text></View>
          </View>
          <View style={styles.tableRowLast}>
            <View style={styles.tableCell}><Text>1</Text></View>
            <View style={styles.tableCell}><Text>{booking.title || 'Mr'}</Text></View>
            <View style={styles.tableCell}><Text>{booking.clientName || 'Customer'}</Text></View>
            <View style={styles.tableCell}><Text>{booking.serviceLevel || 'Standard'}</Text></View>
            <View style={styles.tableCell}><Text>Meet & Greet</Text></View>
            <View style={styles.tableCell}><Text>{booking.addOn || 'None'}</Text></View>
            <View style={styles.tableCellLast}><Text>{booking.notes || 'N/A'}</Text></View>
          </View>
        </View>
        
        {/* Remarks */}
        <View style={styles.remarksBox}>
          <Text style={styles.remarksTitle}>Remarks:</Text>
          <Text style={styles.remarksContent}>
            {booking.remarks || 'Standard departure service with meet & greet assistance. Our greeter will assist you through check-in and security.'}
          </Text>
        </View>
        
        {/* Greeter and Contact Details */}
        <View style={styles.twoColumn}>
          <View style={styles.detailCard}>
            <View style={styles.cardHeader}>
              <Text>Greeter's Details</Text>
            </View>
            <View style={styles.cardBody}>
              <View style={styles.detailRow}>
                <Text style={styles.detailRowStrong}>Name:</Text>
                <Text style={styles.detailRowSpan}>{booking.greeterName || 'TBD'}</Text>
              </View>
              <View style={styles.detailRowLast}>
                <Text style={styles.detailRowStrong}>Contact:</Text>
                <Text style={styles.detailRowSpan}>{booking.greeterContact || 'TBD'}</Text>
              </View>
            </View>
          </View>
          
          <View style={styles.detailCard}>
            <View style={styles.cardHeader}>
              <Text>Contact Person Details</Text>
            </View>
            <View style={styles.cardBody}>
              <View style={styles.detailRow}>
                <Text style={styles.detailRowStrong}>Name:</Text>
                <Text style={styles.detailRowSpan}>{booking.contactPersonName || booking.clientName || 'Customer'}</Text>
              </View>
              <View style={styles.detailRowLast}>
                <Text style={styles.detailRowStrong}>Contact:</Text>
                <Text style={styles.detailRowSpan}>{booking.contactPersonPhone || booking.clientPhone || booking.clientEmail || 'N/A'}</Text>
              </View>
            </View>
          </View>
        </View>
        
        <View style={styles.detailCardFull}>
          <View style={styles.cardHeader}>
            <Text>Driver's Details</Text>
          </View>
          <View style={styles.cardBody}>
            <View style={styles.detailRow}>
              <Text style={styles.detailRowStrong}>Name:</Text>
              <Text style={styles.detailRowSpan}>{booking.driverName || 'TBD'}</Text>
            </View>
            <View style={styles.detailRowLast}>
              <Text style={styles.detailRowStrong}>Registration No:</Text>
              <Text style={styles.detailRowSpan}>{booking.driverRegistration || 'TBD'}</Text>
            </View>
          </View>
        </View>
        
        {/* Product Details */}
        <Text style={styles.sectionHeader}>Product Details</Text>
        <View style={styles.dataTable}>
          <View style={styles.tableRow}>
            <View style={styles.tableHeader}><Text>Service / Add On</Text></View>
            <View style={styles.tableHeader}><Text>Count</Text></View>
            <View style={styles.tableHeader}><Text>Type</Text></View>
            <View style={styles.tableHeader}><Text>Net Amount</Text></View>
            <View style={styles.tableHeader}><Text>VAT%</Text></View>
            <View style={styles.tableHeader}><Text>VAT Amount</Text></View>
            <View style={styles.tableHeaderLast}><Text>Total Charge</Text></View>
          </View>
          <View style={styles.tableRowLast}>
            <View style={styles.tableCell}><Text>Meet & Greet</Text></View>
            <View style={styles.tableCell}><Text>1</Text></View>
            <View style={styles.tableCell}><Text>Service</Text></View>
            <View style={styles.tableCell}><Text>{formatCurrency(basePriceNet)}</Text></View>
            <View style={styles.tableCell}><Text>20%</Text></View>
            <View style={styles.tableCell}><Text>{formatCurrency(baseVatAmount)}</Text></View>
            <View style={styles.tableCellLast}><Text>{formatCurrency(basePriceInclusive)}</Text></View>
          </View>
          {additionalFeesInclusive > 0 && (
            <View style={styles.tableRowLast}>
              <View style={styles.tableCell}><Text>Extra Luggage</Text></View>
              <View style={styles.tableCell}><Text>{booking.baggageCount || 1}</Text></View>
              <View style={styles.tableCell}><Text>Add-on</Text></View>
              <View style={styles.tableCell}><Text>{formatCurrency(additionalFeesNet)}</Text></View>
              <View style={styles.tableCell}><Text>20%</Text></View>
              <View style={styles.tableCell}><Text>{formatCurrency(additionalVatAmount)}</Text></View>
              <View style={styles.tableCellLast}><Text>{formatCurrency(additionalFeesInclusive)}</Text></View>
            </View>
          )}
        </View>
        
        {/* Service Details */}
        <View style={styles.serviceDetailsSection}>
          <Text style={styles.serviceDetailsTitle}>Service Details</Text>
          <View style={styles.serviceInfo}>
            <View style={styles.serviceRow}>
              <Text style={styles.serviceLabel}>Service Level:</Text>
              <Text style={styles.serviceValue}>{booking.serviceLevel || 'Standard'}</Text>
            </View>
            <View style={styles.serviceRow}>
              <Text style={styles.serviceLabel}>Features:</Text>
              <View style={styles.featuresList}>
                {booking.features && booking.features.length > 0 ? (
                  booking.features.map((feature, index) => (
                    <Text key={index} style={styles.featuresListItem}>• {feature}</Text>
                  ))
                ) : (
                  <>
                    <Text style={styles.featuresListItem}>• Meet & Greet at departure</Text>
                    <Text style={styles.featuresListItem}>• Check-in assistance</Text>
                    <Text style={styles.featuresListItem}>• Security fast track</Text>
                    <Text style={styles.featuresListItem}>• Terminal guidance</Text>
                  </>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Pricing Information */}
        <View style={styles.pricingInfoSection}>
          <Text style={styles.pricingInfoTitle}>Pricing Information</Text>
          <View style={styles.pricingInfo}>
            <View style={styles.pricingRow}>
              <Text style={styles.pricingLabel}>Base Price:</Text>
              <Text style={styles.pricingValue}>{formatCurrency(basePriceNet)}</Text>
            </View>
            <View style={styles.pricingRow}>
              <Text style={styles.pricingLabel}>Pricing Source:</Text>
              <Text style={styles.pricingValue}>{booking.pricingSource || 'Dynamic Pricing System'}</Text>
            </View>
          </View>
        </View>

        {/* Total Amount */}
        <View style={styles.totalSection}>
          <Text style={styles.totalLabel}>TOTAL AMOUNT</Text>
          <Text style={styles.totalItem}>1</Text>
          <Text style={styles.totalItem}>Service</Text>
          <Text style={styles.totalItem}>{formatCurrency(subtotalNet)}</Text>
          <Text style={styles.totalItem}>20%</Text>
          <Text style={styles.totalItem}>{formatCurrency(totalVatAmount)}</Text>
          <Text style={styles.totalItem}>{formatCurrency(totalAmountPaid)}</Text>
        </View>
        
        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.vatInfo}>
            <Text>VAT Reg No: 177013321</Text>
          </View>
        </View>
        
      </Page>
    </Document>
  );
};

export default DeparturePDF;